(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const motionEnabled = !reducedMotion.matches && document.body?.dataset.motionEnabled !== 'false';
  const observedReveals = new WeakSet();
  const maskTargetsByTrigger = new WeakMap();
  const revealObserver = motionEnabled
    ? new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
      )
    : null;
  const maskRevealObserver = motionEnabled
    ? new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            (maskTargetsByTrigger.get(entry.target) || []).forEach((target) => {
              target.classList.add('is-visible');
            });
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -6% 0px', threshold: 0.01 }
      )
    : null;

  function initReveals(scope) {
    document.body.classList.toggle('hc-motion-ready', motionEnabled);

    scope.querySelectorAll('[data-hc-reveal]').forEach((element) => {
      if (observedReveals.has(element)) return;
      observedReveals.add(element);

      if (!motionEnabled || !revealObserver) {
        element.classList.add('is-visible');
      } else if (element.classList.contains('hc-mask-reveal') && maskRevealObserver) {
        const trigger = element.closest('[data-hc-section]') || element.parentElement;
        const maskTargets = maskTargetsByTrigger.get(trigger) || [];
        maskTargets.push(element);
        maskTargetsByTrigger.set(trigger, maskTargets);
        maskRevealObserver.observe(trigger);
      } else {
        revealObserver.observe(element);
      }
    });
  }

  function activeVideo(wrap) {
    const videos = [...wrap.querySelectorAll('video')];
    return videos.find((video) => {
      const styles = window.getComputedStyle(video.closest('.hc-hero__video') || video);
      return styles.display !== 'none' && video.getClientRects().length > 0;
    }) || videos[0];
  }

  function initVideoWrap(wrap) {
    if (wrap.dataset.hcInitialized === 'true') return;
    wrap.dataset.hcInitialized = 'true';

    const videos = [...wrap.querySelectorAll('video')];
    const shouldAutoplay = wrap.dataset.autoplay === 'true';

    videos.forEach((video) => {
      if (reducedMotion.matches) {
        video.autoplay = false;
        video.removeAttribute('autoplay');
        video.pause();
      }
      video.addEventListener('error', () => wrap.classList.add('has-media-error'));
      video.addEventListener('loadeddata', () => wrap.classList.remove('has-media-error'));
    });

    if (!('IntersectionObserver' in window)) return;

    const visibilityObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = activeVideo(wrap);
          if (!video) return;

          if (!entry.isIntersecting) {
            video.pause();
            return;
          }

          if (shouldAutoplay && !reducedMotion.matches) {
            const playRequest = video.play();
            if (playRequest && typeof playRequest.catch === 'function') {
              playRequest.catch(() => {});
            }
          }
        });
      },
      { rootMargin: '120px', threshold: 0.2 }
    );
    visibilityObserver.observe(wrap);
  }

  function initDrinkModes(section) {
    if (section.dataset.hcInitialized === 'true') return;
    section.dataset.hcInitialized = 'true';
    section.classList.add('hc-enhanced');

    const tabs = [...section.querySelectorAll('[data-hc-drink-tab]')];
    const panels = [...section.querySelectorAll('[data-hc-drink-panel]')];
    if (!tabs.length || tabs.length !== panels.length) return;

    function activate(index, moveFocus = false) {
      tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === index;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        panels[tabIndex].classList.toggle('is-active', active);
        panels[tabIndex].hidden = !active;
      });

      if (moveFocus) tabs[index].focus();
    }

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(index));
      tab.addEventListener('keydown', (event) => {
        let targetIndex;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          targetIndex = (index + 1) % tabs.length;
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          targetIndex = (index - 1 + tabs.length) % tabs.length;
        } else if (event.key === 'Home') {
          targetIndex = 0;
        } else if (event.key === 'End') {
          targetIndex = tabs.length - 1;
        }

        if (targetIndex === undefined) return;
        event.preventDefault();
        activate(targetIndex, true);
        tabs[targetIndex].scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', inline: 'center', block: 'nearest' });
      });
    });

    activate(0);
  }

  function initVariantStory(section) {
    if (section.dataset.hcInitialized === 'true') return;
    section.dataset.hcInitialized = 'true';

    const dataNode = section.querySelector('[data-hc-variant-data]');
    const fields = [...section.querySelectorAll('[data-hc-option-position]')];
    if (!dataNode || !fields.length) return;

    let variants;
    try {
      variants = JSON.parse(dataNode.textContent);
    } catch (_error) {
      return;
    }

    const image = section.querySelector('[data-hc-variant-image], [data-hc-variant-image-wrap] img');
    const link = section.querySelector('[data-hc-variant-link]');
    const variantName = section.querySelector('[data-hc-variant-name]');

    function selectedValues() {
      return fields.map((field) => field.querySelector('[data-hc-option-value].is-active')?.dataset.hcOptionValue);
    }

    function updateVariant() {
      const values = selectedValues();
      const variant = variants.find((candidate) =>
        values.every((value, index) => candidate.options[index] === value)
      );
      if (!variant) return;

      if (variantName) variantName.textContent = variant.public_title || variant.title;
      if (link) {
        const base = link.href.split('?')[0];
        link.href = variant.url || `${base}?variant=${variant.id}`;
      }

      if (image && variant.featured_image?.src) {
        image.classList.add('is-changing');
        const nextSource = variant.featured_image.src;
        window.setTimeout(() => {
          image.src = nextSource;
          image.srcset = '';
          image.alt = variant.featured_image.alt || variant.title || '';
          image.classList.remove('is-changing');
        }, reducedMotion.matches ? 0 : 180);
      }

      section.dispatchEvent(new CustomEvent('hc:variant-change', {
        bubbles: true,
        detail: { variant }
      }));
    }

    fields.forEach((field) => {
      const label = field.querySelector('[data-hc-option-label]');
      field.querySelectorAll('[data-hc-option-value]').forEach((button) => {
        button.addEventListener('click', () => {
          field.querySelectorAll('[data-hc-option-value]').forEach((item) => {
            const active = item === button;
            item.classList.toggle('is-active', active);
            item.setAttribute('aria-pressed', String(active));
          });
          if (label) label.textContent = button.dataset.hcOptionValue;
          updateVariant();
        });
      });
    });
  }

  function initFinalProduct(section) {
    if (section.dataset.hcInitialized === 'true') return;
    section.dataset.hcInitialized = 'true';

    const select = section.querySelector('[data-hc-final-variant]');
    if (!select) return;

    const price = section.querySelector('[data-hc-final-price]');
    const submit = section.querySelector('[data-hc-final-submit]');
    const submitLabel = section.querySelector('[data-hc-final-submit-label]');
    const availableLabel = section.dataset.addLabel || 'Add to cart';
    const soldOutLabel = section.dataset.soldOutLabel || 'Sold out';

    function update() {
      const option = select.options[select.selectedIndex];
      const available = option.dataset.available === 'true';
      if (price) price.textContent = option.dataset.price;
      if (submit) submit.disabled = !available;
      if (submitLabel) submitLabel.textContent = available ? availableLabel : soldOutLabel;
    }

    select.addEventListener('change', update);
    update();
  }

  function initialise(scope = document) {
    initReveals(scope);
    scope.querySelectorAll('[data-hc-video-wrap]').forEach(initVideoWrap);
    scope.querySelectorAll('[data-hc-drink]').forEach(initDrinkModes);
    scope.querySelectorAll('[data-hc-variant-story]').forEach(initVariantStory);
    scope.querySelectorAll('[data-hc-final-product]').forEach(initFinalProduct);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise());
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
